/**
 * A PORTALLED SURFACE MUST NOT ACTIVATE WHAT IT WAS OPENED FROM.
 *
 * THE BUG. `Table` makes a row a pointer target with an `onClick` on the `<tr>`, and excludes the
 * cells that mean something else by marking them `data-row-navigate-exclude` — which
 * `shouldActivateRow` looks for with `closest()`. `closest()` walks the **DOM**. A React portal
 * appends its content to `document.body`, so the DOM path from a menu item runs to `<body>` and
 * never meets the excluded cell — but React dispatches synthetic events along the **React tree**,
 * where the menu is still a child of that cell, of that `<td>`, of that `<tr>`. So choosing
 * "Slett" from a row's menu ran the row's handler and navigated to the contact instead of
 * deleting it. Every automated test passed: in a rendered tree the menu item is exactly where it
 * claims to be.
 *
 * WHY THE FIX IS HERE AND NOT IN `Table`. Two narrower repairs were available and both are the
 * kind that has to be made again:
 *
 *   * adding `[role="menuitem"]` to `Table`'s `INTERACTIVE` selector fixes menus and nothing else
 *     — the next portalled surface that is not a menu (a select, a suggestion panel, a time
 *     picker, a dialog) brings the same bug back under a different role;
 *   * marking each portal with `data-row-navigate-exclude` teaches `Table`'s vocabulary to six
 *     components that should not have to know a table exists.
 *
 * The general statement is the one below: a surface that has been portalled out of the tree it
 * was opened from stops the click at its own root, so no ancestor in the React tree — a row, a
 * card, a list item, anything written after this — ever sees a click that happened inside it.
 * It repairs every table at once, including the ones not yet written.
 *
 * WHAT IT DOES NOT BREAK. `stopPropagation` on a React synthetic event stops the ascent through
 * the React tree only. Handlers on the same element still run, handlers below it have already
 * run, and Radix's dismiss behaviour listens for native `pointerdown` on `document`, which this
 * never sees. Click-outside-to-close, item selection and focus management are all untouched.
 */
import type { MouseEvent as ReactMouseEvent } from 'react';

/**
 * Put this on **every direct child of a `Portal`** — a Radix dialog renders its overlay and its
 * panel as siblings, and each one is its own root in `document.body`, so one handler cannot cover
 * both. `portal-isolation.spec.ts` reads the source back and fails on a portal child without it.
 */
export function stopPortalClick(event: ReactMouseEvent): void {
  event.stopPropagation();
}
